import { Box, Stack, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { GroupType } from '@schooly/api';
import {
  CircleIconButton,
  Counter,
  ListRow,
  MinusIcon,
  TagSubject,
  theme,
  TypographyWithOverflowHint,
} from '@schooly/style';
import React, { FC, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { RegisterScope, RosterGroup } from './types';
import { getGroupScopeCounts } from './utils/registerCounts';

type RegisterGroupRowProps = {
  group: RosterGroup;
  scope: RegisterScope;
  scopeLabel: string;
  onRemove?: () => void;
  sx?: SxProps<Theme>;
};

export const RegisterGroupRow: FC<RegisterGroupRowProps> = ({
  group,
  scope,
  scopeLabel,
  onRemove,
  sx,
}) => {
  const { formatMessage } = useIntl();

  const { trackedCount, untrackedAgeGroups, untrackedCount, totalCount } = useMemo(
    () => getGroupScopeCounts(group, scope),
    [group, scope],
  );

  const hasUntrackedStudents = untrackedCount > 0;
  const ageGroupsLabel = untrackedAgeGroups.map((entry) => entry.age_group.name).join(', ');

  return (
    <ListRow
      variant="compact"
      onClick={onRemove}
      title={
        <TypographyWithOverflowHint
          className="list-row-title"
          variant="h3"
          color="common.grey2"
          noWrap
          component={Link}
          to={`/groups/${group.group_id}`}
          target="_blank"
          onClick={(e) => e.stopPropagation()}
          sx={{ '&:hover': { textDecoration: 'underline' } }}
        >
          {group.name}
        </TypographyWithOverflowHint>
      }
      titleAdornment={
        <Stack direction="row" alignItems="center" gap={0.5} ml={0.5} flexShrink={0}>
          <Counter
            className="tracked-counter"
            disabled
            sx={{
              minWidth: 16,
              maxHeight: 16,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ml: 0,
            }}
          >
            <Typography variant="body3" color="inherit">
              {trackedCount}
            </Typography>
          </Counter>
          {hasUntrackedStudents && (
            <Tooltip
              title={
                <Stack gap={2}>
                  <Typography>
                    {formatMessage(
                      { id: 'attendance-UntrackedStudentsTooltip-Total' },
                      { total: totalCount },
                    )}
                  </Typography>
                  <Typography>
                    {formatMessage(
                      { id: 'attendance-UntrackedStudentsTooltip-Untracked' },
                      { count: untrackedCount, scope: scopeLabel, ageGroups: ageGroupsLabel },
                    )}
                  </Typography>
                </Stack>
              }
              componentsProps={{ tooltip: { sx: { padding: 1.25, width: 180 } } }}
            >
              <Counter
                className="untracked-counter"
                styleType="attentionOutlined"
                sx={{
                  minWidth: 16,
                  maxHeight: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ml: 0,
                }}
              >
                <Typography variant="body3" color="inherit">
                  {untrackedCount}
                </Typography>
              </Counter>
            </Tooltip>
          )}
        </Stack>
      }
      subtitle={
        (group.subject || group.group_type === GroupType.TutorGroup) && (
          <Box
            sx={(muiTheme) => ({
              minWidth: 200,
              maxWidth: 200,
              [muiTheme.breakpoints.only('lg')]: { minWidth: 150, maxWidth: 150 },
            })}
          >
            <TagSubject
              sx={{
                maxWidth: '100%',
                '& .MuiTypography-root': { overflow: 'hidden', textOverflow: 'ellipsis' },
              }}
            >
              {group.group_type === GroupType.TutorGroup
                ? formatMessage({ id: 'groups-TutorGroup' })
                : group.subject?.name}
            </TagSubject>
          </Box>
        )
      }
      actions={
        onRemove ? (
          <CircleIconButton
            className="actionIcon"
            size="small"
            sx={{
              position: 'absolute',
              right: 4,
              color: theme.palette.common.light2,
            }}
          >
            <MinusIcon />
          </CircleIconButton>
        ) : undefined
      }
      sx={{
        '.MuiCardActionArea-focusHighlight': { background: 'transparent' },
        '&:hover': {
          backgroundColor: theme.palette.common.lightBg,
          '.list-row-title, .list-row-right-content, .tag-subject': {
            color: theme.palette.primary.main,
          },
          '.tracked-counter': {
            backgroundColor: 'transparent',
            color: theme.palette.common.grey2,
            borderColor: theme.palette.common.grey3,
          },
          '.untracked-counter': {
            backgroundColor: theme.palette.common.orange,
            color: theme.palette.common.white,
            borderColor: theme.palette.common.orange,
          },
          '.actionIcon': {
            color: theme.palette.common.light2,
            background: theme.palette.primary.main,
          },
        },
        ...sx,
      }}
    />
  );
};
